import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import {
  IonButton,
  IonButtons,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonLoading,
  IonModal,
  IonTitle,
  IonToolbar,
} from '@ionic/react'
import { useDispatch, useSelector } from 'react-redux'

import { PDFViewer } from '../../../components/pdfViewer/PDFViewer.component'
import { getFillablePDFsSelector } from '../selectors'
import { getFillablePDFOptionsSelector } from '../../options/selectors'
import { getServiceIdSelector } from '../../user/selectors'

import { loadPdfFromDevice, setCustomInputs } from '../store/fillablePDFs.actions'
import { FillablePdfsService } from '../services/fillablePdfs.service'
import { fillPdfWithCustomInputs } from '../../../utilities/pdfFill.util'
import type { ICustomInput } from '../store/fillablePDFs.types'
import { PdfRenderStatus } from '../store/fillablePDFs.constants'
import { buildSignatureFieldsFromFormDef, SignatureOverlay } from '../../../utilities/pdf.util'
import SignatureCanvasPad from '../../signatures/signatureCanvas/SignatureCanvasPad'
import { useTranslation } from 'react-i18next'
import { useWindowSize } from '../../../hooks'
import { safeJsonParse } from '../../../utilities/safeParse.util'
import { getStyleForPHI } from '../../../components/phi/phi.component'
import { addOutline, removeOutline } from 'ionicons/icons'
import Logger from '../../../services/logger.service'
import { trimSignatureDataUrl } from '../../../utilities/signature.util'

const ReviewAndSign = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { id, customInputs, pdfUrl, pdfLoading, pdfError } = useSelector(getFillablePDFsSelector)
  const { metadata } = useSelector(getFillablePDFOptionsSelector)
  const serviceId = useSelector(getServiceIdSelector)
  const { width: windowWidth, height: windowHeight } = useWindowSize({ debounceMs: 300 })
  const [filledUrl, setFilledUrl] = useState<string | null>(null)
  const [isGeneratingFilledPdf, setIsGeneratingFilledPdf] = useState(false)
  const [useFallbackPdf, setUseFallbackPdf] = useState(false)
  const [pdfRenderStatus, setPdfRenderStatus] = useState<PdfRenderStatus>(PdfRenderStatus.IDLE)
  const [hasRenderedOnce, setHasRenderedOnce] = useState(false)
  const [activeSignatureElementId, setActiveSignatureElementId] = useState<string | null>(null)
  const sigCanvasRef = useRef<any>(null)
  const canvasContainerRef = useRef<HTMLDivElement>(null)
  const [canvasWidth, setCanvasWidth] = useState(300)
  const [canvasHeight, setCanvasHeight] = useState(200)
  const [zoomScale, setZoomScale] = useState(1)
  const [signatureWasCleared, setSignatureWasCleared] = useState(false)

  const handlePdfFillError = () => {
    setUseFallbackPdf(true)
    setIsGeneratingFilledPdf(false)
  }

  const formDef = useMemo(() => {
    const row = metadata?.find((f: any) => f.FormNameWithId === id)
    if (!row?.Data) return null
    try {
      return safeJsonParse(row.Data)
    } catch {
      return null
    }
  }, [metadata, id])

  const pdfDocId = useMemo(() => {
    const row = metadata?.find((f: any) => f.FormNameWithId === id)
    return row?.FormNameWithId ?? null
  }, [metadata, id])

  useEffect(() => {
    if (!pdfDocId) return
    dispatch(loadPdfFromDevice(pdfDocId))
  }, [pdfDocId, dispatch])

  useEffect(() => {
    setPdfRenderStatus(PdfRenderStatus.IDLE)
    setHasRenderedOnce(false)
  }, [pdfDocId])

  const signatureFields: SignatureOverlay[] = useMemo(() => {
    if (!formDef) return []
    return buildSignatureFieldsFromFormDef(formDef)
  }, [formDef])

  useEffect(() => {
    let isCancelled = false
    const hasCustomInputs = (customInputs?.length ?? 0) > 0

    if (!hasCustomInputs) {
      setUseFallbackPdf(false)
      setIsGeneratingFilledPdf(false)
      setFilledUrl(null)
      return
    }

    if (!pdfDocId || !serviceId) {
      setIsGeneratingFilledPdf(false)
      return
    }

    setHasRenderedOnce(false)
    setIsGeneratingFilledPdf(true)

    const run = async () => {
      setUseFallbackPdf(false)

      try {
        const localService = new FillablePdfsService(serviceId)
        const doc = await localService.getFillablePdfBlobDoc(pdfDocId)
        if (!doc || typeof doc.entity !== 'string') {
          if (!isCancelled) {
            handlePdfFillError()
          }
          return
        }

        const filledBytes = await fillPdfWithCustomInputs(doc.entity, customInputs)

        const u8 = new Uint8Array(filledBytes)
        const blob = new Blob([u8.buffer], { type: 'application/pdf' })
        const url = URL.createObjectURL(blob)

        if (isCancelled) {
          URL.revokeObjectURL(url)
          return
        }

        setFilledUrl(url)
      } catch (error) {
        if (!isCancelled) {
          handlePdfFillError()
          Logger.errorObj('Failed to prepare filled PDF preview', error)
        }
      } finally {
        if (!isCancelled) {
          setIsGeneratingFilledPdf(false)
        }
      }
    }

    run()

    return () => {
      isCancelled = true
    }
  }, [pdfDocId, serviceId, customInputs])

  useEffect(() => {
    if (filledUrl) {
      setIsGeneratingFilledPdf(false)
    }
    return () => {
      if (filledUrl) URL.revokeObjectURL(filledUrl)
    }
  }, [filledUrl])

  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl)
    }
  }, [pdfUrl])

  const hasCustomInputs = (customInputs?.length ?? 0) > 0
  const requiresFilledPdf = hasCustomInputs && !useFallbackPdf
  const resolvedPdfUrl = requiresFilledPdf ? filledUrl : pdfUrl || null
  const isPreparingFilledPdf = requiresFilledPdf && !filledUrl
  const shouldShowLoadingOverlay =
    !hasRenderedOnce &&
    !pdfError &&
    (pdfLoading ||
      isGeneratingFilledPdf ||
      isPreparingFilledPdf ||
      !resolvedPdfUrl ||
      pdfRenderStatus === PdfRenderStatus.LOADING)

  const openSignatureModal = (elementId: string) => {
    setActiveSignatureElementId(elementId)
    setSignatureWasCleared(false)
  }

  const closeSignatureModal = () => {
    setActiveSignatureElementId(null)
  }

  const handlePdfRenderStatus = useCallback((status: PdfRenderStatus) => {
    setPdfRenderStatus(status === PdfRenderStatus.ERROR ? PdfRenderStatus.COMPLETE : status)
    if (status === PdfRenderStatus.COMPLETE) {
      setHasRenderedOnce(true)
    }
  }, [])

  const saveSignature = () => {
    if (!activeSignatureElementId) return
    if (!sigCanvasRef.current) return

    const existingInput = (customInputs ?? []).find(
      (ci: ICustomInput) => ci.fieldType === 'signature' && String(ci.questionId) === String(activeSignatureElementId)
    )

    const hasExistingSignature = !!existingInput
    const canvasIsEmpty = sigCanvasRef.current.isEmpty?.()

    if (canvasIsEmpty && signatureWasCleared) {
      const nextInputs = (customInputs ?? []).filter(
        (ci: ICustomInput) =>
          !(ci.fieldType === 'signature' && String(ci.questionId) === String(activeSignatureElementId))
      )

      dispatch(setCustomInputs(nextInputs))
      closeSignatureModal()
      return
    }

    if (canvasIsEmpty && hasExistingSignature) {
      closeSignatureModal()
      return
    }

    const originalDataUrl: string = sigCanvasRef.current.toDataURL('image/png')
    const trimmedDataUrl = trimSignatureDataUrl(sigCanvasRef.current)

    if (!originalDataUrl || !trimmedDataUrl) return

    const nextInputs: ICustomInput[] = [
      ...(customInputs ?? []).filter((ci: ICustomInput) => ci.questionId !== activeSignatureElementId),
      {
        questionId: activeSignatureElementId,
        question: activeSignatureElementId,
        answerId: {
          originalDataUrl,
          trimmedDataUrl,
        },
        fieldType: 'signature',
      } as ICustomInput,
    ]

    dispatch(setCustomInputs(nextInputs))
    sigCanvasRef.current.clear()
    closeSignatureModal()
  }

  const clearSignature = () => {
    if (!activeSignatureElementId) return

    sigCanvasRef.current?.clear?.()
    setSignatureWasCleared(true)
  }

  const existingSignature = useMemo(() => {
    if (!activeSignatureElementId) return null

    const match = customInputs?.find(
      (ci: ICustomInput) => ci.fieldType === 'signature' && String(ci.questionId) === String(activeSignatureElementId)
    )

    if (!match) return null

    if (typeof match.answerId === 'string') {
      return match.answerId
    }

    return match.answerId?.originalDataUrl ?? null
  }, [customInputs, activeSignatureElementId])

  const updateCanvasSize = () => {
    const container = canvasContainerRef.current
    if (!container) return

    const rect = container.getBoundingClientRect()
    const nextWidth = Math.max(260, Math.floor(rect.width - 8))
    const nextHeight = Math.max(180, Math.floor(rect.height - 8))

    setCanvasWidth(nextWidth)
    setCanvasHeight(nextHeight)

    setTimeout(() => {
      loadExistingSignatureIntoCanvas()
    }, 120)
  }

  const loadSignatureCentered = (dataUrl: string) => {
    if (!sigCanvasRef.current) return

    const canvas = sigCanvasRef.current.getCanvas?.()
    const ctx = canvas?.getContext('2d')
    if (!canvas || !ctx) return

    const img = new Image()

    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      const scale = Math.min(canvas.width / img.width, canvas.height / img.height) * 0.8
      const drawWidth = img.width * scale
      const drawHeight = img.height * scale
      const x = (canvas.width - drawWidth) / 2
      const y = (canvas.height - drawHeight) / 2

      ctx.drawImage(img, x, y, drawWidth, drawHeight)
    }

    img.src = dataUrl
  }

  const loadExistingSignatureIntoCanvas = () => {
    if (!existingSignature) return

    try {
      loadSignatureCentered(existingSignature)
    } catch (e) {
      Logger.errorObj('Failed to load existing signature into canvas', e)
    }
  }

  useEffect(() => {
    if (!activeSignatureElementId) return

    const timeout = setTimeout(() => {
      updateCanvasSize()
    }, 50)

    return () => clearTimeout(timeout)
  }, [activeSignatureElementId, windowWidth, windowHeight])

  return (
    <>
      <IonHeader>
        <IonToolbar color='light'>
          <IonTitle>{t('fillablePDFs.reviewAndSign')}</IonTitle>
          <IonButtons slot='end'>
            <IonButton
              data-testid='PDFZoomOut'
              className='btn btn-primary'
              expand='block'
              id='zoom-out-button'
              onClick={() => setZoomScale((prev) => Math.max(1, prev - 0.25))}>
              <IonIcon icon={removeOutline} />
            </IonButton>
            <IonButton
              data-testid='PDFZoomIn'
              className='btn btn-primary'
              expand='block'
              id='zoom-in-button'
              onClick={() => setZoomScale((prev) => Math.min(3, prev + 0.25))}>
              <IonIcon icon={addOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen>
        <IonLoading
          data-testid='FillablePdfLoader'
          isOpen={shouldShowLoadingOverlay}
          animated
          showBackdrop
          spinner='crescent'
        />

        {pdfError && (
          <div className='pdf-error' style={{ padding: '2rem', textAlign: 'center' }}>
            <p>{t('fillablePdf.errorLoadingPdf', 'Unable to load PDF. Please check your connection and try again.')}</p>
          </div>
        )}

        <div className='pdf-shell' data-private={true} style={getStyleForPHI(true)}>
          <div className='pdf-viewport'>
            {resolvedPdfUrl && !pdfError && (
              <PDFViewer
                fileUrl={resolvedPdfUrl}
                signatureFields={signatureFields}
                customInputs={customInputs}
                onClickSignature={openSignatureModal}
                rerenderOnCustomInputs={!requiresFilledPdf}
                onRenderStatusChange={handlePdfRenderStatus}
                zoomScale={zoomScale}
              />
            )}
          </div>
        </div>

        <IonModal
          className='SignatureModal'
          isOpen={!!activeSignatureElementId}
          onDidDismiss={closeSignatureModal}
          onDidPresent={() => {
            updateCanvasSize()
            setTimeout(() => {
              loadExistingSignatureIntoCanvas()
            }, 150)
          }}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>{t('fillablePDFs.signature')}</IonTitle>
            </IonToolbar>
          </IonHeader>
          <IonContent>
            <div ref={canvasContainerRef} className='signature-canvas-modal-body'>
              <SignatureCanvasPad
                isPHI={true}
                isCanvasVisible
                canvasRef={sigCanvasRef}
                isCanvasExpandable={false}
                canvasWidth={canvasWidth}
                canvasHeight={canvasHeight}
                minWidth={3}
                maxWidth={3}
                penColor='#000'
                canvasContent={{
                  canvasHeader: 'Signature',
                  canvasData: existingSignature ?? undefined,
                }}
                onClear={clearSignature}
              />
            </div>
          </IonContent>
          <IonFooter className='ion-no-border'>
            <IonToolbar color='light' className='p-10'>
              <IonButtons>
                <IonButton
                  data-testid='SignatureCancel'
                  className='btn btn-secondary mr-8'
                  expand='block'
                  id='multiselect-modal-cancel-button'
                  onClick={closeSignatureModal}>
                  {t('common.buttons.Cancel')}
                </IonButton>
                <IonButton
                  data-testid='SignatureClear'
                  className='btn mr-8'
                  color='danger'
                  fill='outline'
                  expand='block'
                  id='multiselect-modal-clear-button'
                  onClick={clearSignature}>
                  {t('common.buttons.Clear')}
                </IonButton>
                <IonButton
                  data-testid='SignatureSave'
                  className='btn btn-primary'
                  expand='block'
                  onClick={saveSignature}
                  id='multiselect-modal-save-button'>
                  {t('common.buttons.Save')}
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonFooter>
        </IonModal>
      </IonContent>
    </>
  )
}

export default ReviewAndSign
